import { useEffect, useState } from "react";
import { UserPlus, Trash2, ShieldOff, Loader2, X, Crown, Mail, ShieldCheck, ShieldAlert, KeyRound } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";

function InviteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      onCreated();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div data-testid="invite-modal" className="bg-[#131C31] w-full max-w-md rounded-2xl border border-slate-800 shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="font-display font-bold text-xl">Add Team Member</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Full Name</label>
            <input data-testid="invite-name-input" required className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="invite-email-input" type="email" required className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Temporary Password (min 8 chars)</label>
            <input data-testid="invite-password-input" required className={inputCls} value={form.password} onChange={(e) => set("password", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select data-testid="invite-role-select" className={inputCls} value={form.role} onChange={(e) => set("role", e.target.value)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <p className="text-xs text-slate-500">They'll be required to set up MFA on their first login.</p>
          {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="invite-submit-button" type="submit" disabled={saving} className="btn-primary w-full rounded-lg py-2.5 inline-flex items-center justify-center gap-2 text-sm">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
          </button>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ target, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setError("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${target.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${target.email}`);
      onDone();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div data-testid="reset-password-modal" className="bg-[#131C31] w-full max-w-md rounded-2xl border border-slate-800 shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="font-display font-bold text-xl inline-flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-400" /> Reset Password</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <p className="text-sm text-slate-400">Set a new password for <span className="text-white font-medium">{target.name}</span> ({target.email}). Share it with them securely; they can change it later in Settings.</p>
          <div>
            <label className={labelCls}>New Password (min 8 chars)</label>
            <input data-testid="reset-pw-input" type="text" required className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <input data-testid="reset-pw-confirm-input" type="text" required className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="reset-pw-submit-button" type="submit" disabled={saving} className="btn-primary w-full rounded-lg py-2.5 inline-flex items-center justify-center gap-2 text-sm">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Reset Password
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const resetMfa = async (u) => {
    if (!window.confirm(`Reset MFA for ${u.email}? They'll re-enroll on next login.`)) return;
    try {
      await api.post(`/users/${u.id}/reset-mfa`);
      toast.success("MFA reset");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <AppLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">Team</h1>
            <p className="text-slate-400 text-sm mt-1">Manage who can access the CRM.</p>
          </div>
          <button data-testid="invite-user-button" onClick={() => setInviteOpen(true)} className="btn-primary rounded-lg px-5 py-2.5 inline-flex items-center gap-2 text-sm">
            <UserPlus className="w-4 h-4" /> Add Member
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400 gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : (
          <div className="rounded-xl border border-slate-800 bg-[#131C31] divide-y divide-slate-800" data-testid="users-list">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-4 px-5 py-4">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center shrink-0">
                  {u.role === "admin" ? <Crown className="w-5 h-5 text-amber-400" /> : <Mail className="w-4 h-4 text-slate-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white truncate">{u.name} {u.id === user.id && <span className="text-xs text-slate-500">(you)</span>}</p>
                  <p className="text-sm text-slate-400 truncate">{u.email}</p>
                </div>
                <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-slate-700 text-slate-400 hidden sm:inline">{u.role}</span>
                {u.mfa_enabled ? (
                  <span className="text-xs inline-flex items-center gap-1 text-emerald-400"><ShieldCheck className="w-4 h-4" /> MFA</span>
                ) : (
                  <span className="text-xs inline-flex items-center gap-1 text-amber-400"><ShieldAlert className="w-4 h-4" /> Pending</span>
                )}
                <div className="flex items-center gap-1">
                  <button data-testid={`reset-password-${u.id}`} title="Reset password" onClick={() => setResetTarget(u)} className="p-2 text-slate-400 hover:text-amber-400 rounded-lg hover:bg-slate-800">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  {u.mfa_enabled && (
                    <button title="Reset MFA" onClick={() => resetMfa(u)} className="p-2 text-slate-400 hover:text-amber-400 rounded-lg hover:bg-slate-800">
                      <ShieldOff className="w-4 h-4" />
                    </button>
                  )}
                  {u.id !== user.id && (
                    <button data-testid={`delete-user-${u.id}`} title="Delete" onClick={() => remove(u)} className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-slate-800">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onCreated={() => { setInviteOpen(false); load(); }} />}
      {resetTarget && <ResetPasswordModal target={resetTarget} onClose={() => setResetTarget(null)} onDone={() => setResetTarget(null)} />}
    </AppLayout>
  );
}
